<template><div><h1 id="konzept" tabindex="-1"><a class="header-anchor" href="#konzept"><span>Konzept</span></a></h1>
<h2 id="_1-mittelbeschaffungs-system" tabindex="-1"><a class="header-anchor" href="#_1-mittelbeschaffungs-system"><span>1. Mittelbeschaffungs-System</span></a></h2>
<p>Die Software soll alle administrativen Tätigkeiten unterstützen, die bei einer Non Profit Organisation (NPO) im Bereich der Mittelbeschaffung (FUNdraising and TRADing) anfallen. Die NPO nimmt Geld durch Beiträge, Spenden, Warenverkauf, etc. ein. Daraus ergeben sich folgende Hauptbewegungen:</p>
<div class="language-txt line-numbers-mode" data-highlighter="prismjs" data-ext="txt"><pre v-pre><code><span class="line">      NPO             bittet um Geld  >>        Person</span>
<span class="line">      NPO     &lt;&lt;        schickt Geld            Person</span>
<span class="line">      NPO             liefert Ware    >>        Person</span>
<span class="line">      NPO     &lt;&lt;        bezahlt Ware            Person</span>
<span class="line"></span></code></pre>
<div class="line-numbers" aria-hidden="true" style="counter-reset:line-number 0"><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div></div></div><p>Alle anderen Tätigkeiten können als interne Dienstleistungen betrachtet werden, die auf den Erhalt und das Funktionieren dieser vier Hauptbewegungen ausgerichtet sind.</p>
<p>Selbstverständlich wird ein gut funktionierendes System zur Unterstützung der Warenbeschaffung, des Warenverkaufs, des Zahlungsflusses, etc. benötigt. Diese Bereiche können bei einer NPO nicht grundsätzlich anders organisiert sein als bei anderen Organisationen oder Firmen, die Versandhandel betreiben. Der Versandhandels-Bereich von funtrade unterscheidet sich auch nur insofern von herkömmlichen Systemen, als dieser vollumfänglich mit den anderen Bereichen verzahnt ist. Deshalb sei hier vor allem das Konzept der Informationsspeicherung und dessen Umsetzung in der Software dargestellt.</p>
<h2 id="_2-kontakte-schaffen-pflegen-nutzen" tabindex="-1"><a class="header-anchor" href="#_2-kontakte-schaffen-pflegen-nutzen"><span>2. Kontakte schaffen, pflegen, nutzen</span></a></h2>
<p>Je mehr Personenkontakte die NPO hat und pflegt, um so grösser der Erfolg. Wegen der sich ständig ändernden und erneuernden Bevölkerung wird die NPO gelegentlich neue Adressen kaufen oder mieten müssen, um immer wieder Menschen für die Idee der NPO zu gewinnen. Ebenso wichtig ist es jedoch, die bestehenden Kontakte zu erhalten bzw. gut zu nutzen. Gut genutzt bedeutet in diesem Zusammenhang, dass möglichst viele Personen, die der NPO bekannt sind, Mitglied oder Pate und Spender und Warenkäufer geworden sind. Es versteht sich, dass die Kontaktpflege - also die Adressverwaltung - ein zentrales Thema ist.</p>
<p>Um das Ziel der optimalen Nutzung einer Adresse zu erreichen, wäre es sinnvoll, beispielsweise folgende Fragen beantworten zu können:</p>
<ul>
<li>Wer hat in welchem Zeitraum wieviel gespendet?</li>
<li>Wer hat welche Ware aufgrund welcher Werbemittel gekauft?</li>
<li>Wer hat auf welchen Spendenaufruf mit einer Spende in welcher Höhe reagiert?</li>
<li>Wer hat ein bestimmtes Mailing erhalten, nicht darauf reagiert, wohl aber auf ein anderes?</li>
</ul>
<p>Um einen Kontakt möglichst gut nutzen zu können, muss eine Geschichte des Kontaktes zwischen NPO und dem Adressaten vorliegen. Doch wie soll man es bei z.B. über einer Million Adressen in der Kartei schaffen, Kontaktgeschichten zu speichern - und dann noch so, dass die historischen Daten abfragbar bzw. verwertbar bleiben?</p>
<p>Zunächst ist zu bedenken, dass nicht zu allen Adressen eine gleichermassen differenzierte und detaillierte Kontaktgeschichte vorliegen muss. Fundraising umfasst bei den meisten NPO verschiedene Formen der Kontaktpflege. Die Unterscheidung zwischen kollektivem und individuellem Marketing ist hier hilfreich.</p>
<h2 id="_3-kollektives-und-individuelles-marketing" tabindex="-1"><a class="header-anchor" href="#_3-kollektives-und-individuelles-marketing"><span>3. Kollektives und individuelles Marketing</span></a></h2>
<p>Fundraising-Aktivitäten lassen sich aus zwei Gesichtspunkten gliedern, nämlich</p>
<ul>
<li>
<p>nach inhaltlichen Kriterien bzw. nach der Art der eingesetzten Medien (adressierte und unadressierte Spendenaufrufe, Warenkataloge, Mitgliedschafts- oder Patenschaftsausweise, Inserate, Radio oder TV-Spots, Telefonaktionen, etc.) und</p>
</li>
<li>
<p>nach der Art der administrativen Arbeit in allen Phasen (Vorbereitung, Abwicklung, Nachbearbeitung, Auswertung).</p>
</li>
</ul>
<p>Bei der Konzeption eines Systems zur Unterstützung möglichst vieler Fundraising-Aktionen ist der letztgenannte Gesichtspunkt relevant. Aus praktisch allen Aktionen resultieren neue Personenkontakte bzw. die Fortsetzung bestehender Kontakte. Diese Kontakte (und somit die Adressen mit den zugehörigen Kontaktgeschichten) müssen gepflegt werden, damit sie nicht abbrechen und möglichst ergiebig werden bzw. bleiben.</p>
<p>Nicht alle Kontakte rechtfertigen den gleichen Pflegeaufwand; dass die intensiven Kontakte mehr und die extensiven, sporadischen weniger Pflege erfordern, ist bei der Informationsverarbeitung zu berücksichtigen.</p>
<p>Zum kollektiven Marketing gehören beispielsweise Spendenaufrufe, Versandhandel und Mitglieder- bzw. Patenschaftswerbungen, wobei Mitglieder- oder Patenschaftswerbung bereits in die Richtung einer individuellen „Kontaktpflege“ (zu verstehen im Sinne der Informationsverarbeitung) geht.</p>
<table>
<thead>
<tr>
<th>kollektives Marketing</th>
<th></th>
<th></th>
<th></th>
<th>individuelles Marketing</th>
</tr>
</thead>
<tbody>
<tr>
<td>extensive Kontaktpflege</td>
<td></td>
<td></td>
<td></td>
<td>intensive Kontaktpflege</td>
</tr>
<tr>
<td>grobes Datenraster</td>
<td></td>
<td></td>
<td></td>
<td>feines Datenraster</td>
</tr>
<tr>
<td>hochautomatisierte Administration</td>
<td></td>
<td></td>
<td></td>
<td>manuelle bzw. halbautomatische Administration</td>
</tr>
<tr>
<td>Spendenaufrufe</td>
<td>&lt;&lt;</td>
<td></td>
<td></td>
<td></td>
</tr>
<tr>
<td>Versandhandel</td>
<td>&lt;&lt;</td>
<td></td>
<td></td>
<td></td>
</tr>
<tr>
<td>Produkteverkauf über Dritte</td>
<td>&lt;&lt;</td>
<td></td>
<td></td>
<td></td>
</tr>
<tr>
<td>Spendenaufrufe</td>
<td>&lt;&lt;</td>
<td></td>
<td></td>
<td></td>
</tr>
<tr>
<td></td>
<td>&lt;&lt;</td>
<td>Mitgliedschafts- u. Patenschaftsprogramme, Abonnemente</td>
<td>&gt;&gt;</td>
<td></td>
</tr>
<tr>
<td></td>
<td></td>
<td></td>
<td>&gt;&gt;</td>
<td>Grossspender- und Sponsorenbetreuung</td>
</tr>
<tr>
<td></td>
<td></td>
<td></td>
<td>&gt;&gt;</td>
<td>Legate-Akquisition</td>
</tr>
<tr>
<td></td>
<td></td>
<td></td>
<td>&gt;&gt;</td>
<td>Gesuchstellungen bei Institutionen</td>
</tr>
</tbody>
</table>
<p>Mitglieder- oder Patenschaftsprogramme sind in der obigen Aufstellung zwischen kollektivem und individuellem Marketing plaziert, weil sie „individueller“ in ihrer Bearbeitung sind, als z.B. reine Spendenaufrufe, denn von Mitgliedern muss man wissen, wann sie was versprochen und was sie davon gehalten haben. Doch Mitglieder oder Paten werden in der Regel „kollektiver“ behandelt, als beispielsweise Grossspender oder Sponsoren.</p>
<p>Die für das kollektive Fundraising relevanten Daten sind in folgenden Strukturen abgelegt:</p>
<ul>
<li>Adress-Stammdaten
<ul>
<li>Anschrift</li>
<li>div. Codes</li>
<li>Ortsgruppenzugehörigkeit</li>
<li>Alter</li>
<li>etc.</li>
</ul>
</li>
<li>Merkmale
<ul>
<li>frei definierbare Attribute, die wahlweise in bestimmten Situationen eine Warnung oder Sperre bewirken</li>
</ul>
</li>
<li>Ereignisse
<ul>
<li>Intialereignis (NPO richtet sich an den Adressaten)</li>
<li>Responseereignis (positive Reaktion (Zahlung oder Auftrag)</li>
<li>Negativereignis (Rücksendung durch die Post oder den Adressaten, dargestellt mittels Rücksende-Codes)</li>
</ul>
</li>
<li>Adressbeziehungen
<ul>
<li>Herstellung theoretisch unbegrenzt vieler Beziehungen zwischen Adressen nach dem Stücklisten-Prinzip, z.B. alle Lehrer einer Schule, Mitarbeiter einer Verwaltung, etc.</li>
</ul>
</li>
<li>Zusagen
<ul>
<li>zugesagte regelmässige Spenden, z.B. Mitgliedschaft oder Projektpartnerschaft</li>
</ul>
</li>
<li>Aufträge</li>
<li>Bestellungen/Käufe (Versandhandel)</li>
<li>Umsätze
<ul>
<li>pro Umsatztyp (z.B. Spenden, Zusagen, Käufe) und Jahr</li>
</ul>
</li>
</ul>
<p>Eine weitere Detaillierung wäre mit vertretbarem Aufwand kaum möglich. Während es im Bereich des kollektiven Marketings genügt zu speichern, wer was wann erhalten bzw. wer worauf in welcher Form reagiert hat, ist es im Bereich des individuellen Marketings wichtig, jeden mündlichen oder schriftlichen Kontakt und jede Vereinbarung festzuhalten. Und da der Zahn der Zeit aus jedem geplanten, künftigen Termin irgendwann einen vergangenen macht, entfällt die Trennung zwischen Terminverwaltung (Erinnerungssystem) und Geschichtsschreibung.</p>
<p>Mündliche oder schriftliche Kontakte bzw. Kontaktpflege-Massnahmen und die sich daraus ergebenden Reaktionen der Kontaktierten lassen sich in folgender Struktur speichern:</p>
<ul>
<li>Aktionen</li>
<li>Datum</li>
<li>Mitarbeiter/in</li>
<li>Art der Aktion bzw. des Kontaktes (z.B. Anruf, Brief, Gesuchstellung, Zusage, Absage, Zahlung, etc.)</li>
<li>Kurzbeschreibung</li>
<li>Betrag (z.B. bei Gesuchstellung oder Zusage)</li>
<li>Adresse</li>
<li>Kontaktperson beim Adressaten</li>
</ul>
<p>Wenn im Bereich des individuellen Marketings Zahlungen erfolgen, werden diese zugleich als Responseereignis und als Aktion 'Zahlungseingang' registriert.</p>
<h2 id="_4-merkmale-ereignisse-und-aktionen" tabindex="-1"><a class="header-anchor" href="#_4-merkmale-ereignisse-und-aktionen"><span>4. Merkmale, Ereignisse und Aktionen</span></a></h2>
<p>Der Unterschied zwischen Merkmalen und Ereignissen ist bedeutsam. Aktionen sind grundsätzlich wie Ereignisse zu verstehen; in funtrade wird zwischen Ereignissen und Aktionen unterschieden, um klar zu vermitteln, dass Ereignisse in allen Bereichen zwischen kollektivem und individuellen Marketing anfallen, während Aktionen nur im Bereich der individuellen Bearbeitung von Kontakten vorkommen. Deshalb gilt das im Verlauf für Ereignisse Gesagte auch für Aktionen.</p>
<p>Wenn jemand abstehende Ohren hat, ist das ein (reizendes) Merkmal. Wenn jemand mit den Ohren wackelt, ist dies ein Ereignis. Dass jemand chinesischsprachig ist, bezeichnen wir als Merkmal, doch wenn in China ein Stuhl umfällt, ist das ein Ereignis.</p>
<p>Beispiele von Merkmalen, die für eine NPO relevant sein können (in funtrade aber z.T. über andere Strukturen erfasst wird, z.B. weiblich ist ein eigener Geschlechtscode):</p>
<ul>
<li>Person gehört zur Schule Xyz</li>
<li>Person ist weiblich</li>
<li>Person ist Warenlieferant</li>
<li>Person ist Konzessionär</li>
<li>Person ist Pate</li>
<li>Person ist als schlechter Zahler bekannt</li>
</ul>
<p>Beispiele von Ereignissen, die für eine NPO relevant sein können:</p>
<ul>
<li>Person hat am 17.7.1999 Fr. 500 gespendet</li>
<li>Person hat Mitgliedschaftsbeitrag bezahlt</li>
<li>Person hat den Xyz-Spendenaufruf erhalten und reagiert</li>
<li>Person hat aus dem Katalog Xyz bestellt</li>
<li>Person hat Artikel 9876.03 gekauft</li>
<li>Person hat reklamiert</li>
</ul>
<p>Der Unterschied zwischen Merkmalen und Ereignissen lässt sich auch mit folgendem Aspekt verdeutlichen: Merkmale bezeichnen Umstände oder Eigenschaften, die pro Person nur in einfacher Form vorkommen (eine Person kann nicht zugleich weiblich und männlich sein). Einer Person können allerdings mehrere Merkmale zugeordnet werden (eine Person kann zugleich weiblich, Patin und Lieferantin sein). Ereignisse hingegen können bei einer Person mehrfach und wiederholt vorkommen (eine Person spendet mehrmals, sie kauft mehrmals die gleiche Ware, erhält mehrmals ein Werbemittel).</p>
<h2 id="_5-umsetzungsbeispiele" tabindex="-1"><a class="header-anchor" href="#_5-umsetzungsbeispiele"><span>5. Umsetzungsbeispiele</span></a></h2>
<p>funtrade basiert auf einer sog. relationalen Datenbank, in der die Datenspeicherung keine strukturellen Hierarchien kennt. Eine solche Datenbank lässt sich jedoch verbildlichend als ein Baukasten von Karteikästen darstellen. Eine ganz wichtige (und meistens auch grösste) ist die Adresskartei. Jede Karte ist wie ein fixes Formular gestaltet, das jene Felder enthält, die pro Adresse nur einmal vorkommen, z.B.:</p>
<ul>
<li>Personen-ID</li>
<li>Name</li>
<li>Strasse</li>
<li>Ort</li>
<li>Geburtsdatum</li>
<li>Eintrittsdatum</li>
<li>Beitragskategorie</li>
</ul>
<p>Solche pro Karte einmal vorkommenden Daten werden als Stammdaten bezeichnet. Nun könnte man auf jeder Adresskarte soviel Platz reservieren, dass darauf auch alle Merkmale und Ereignisse eingetragen werden könnten. Doch dies hätte Nachteile: bei manchen Adressen braucht es viel Platz, bei anderen wenig - und man weiss nicht im voraus, wieviel Raum benötigt wird. Also müsste man bei allen Adressen den geschätzten maximalen Raum freihalten, was Platzverschwendung wäre. Deshalb ist es üblich, beispielsweise Merkmale auf kleine Kärtchen zu schreiben und sie jeweils an die grosse Adresskarte zu heften. Diese Kärtchen können winzig sein - wenn man eine Liste aller vorkommenden Merkmale führt und diese numeriert oder codiert, braucht auf dem angehefteten Kärtchen nur die Nummer oder der Code zu stehen, welche(r) das Merkmal auf der Liste hat.</p>
<p>Das obengenannte Prinzip stellt sich in funtrade so dar:</p>
<p>Die Adresse steht auf der grossen Stammkarte (Pfad: <code v-pre>Adressverwaltung &gt; Adressenstamm</code>)</p>
<p>Die Liste aller vorkommenden Merkmale steht in der Personenmerkmal-Tabelle (Pfad: <code v-pre>Tabellen &gt; Adressverwaltung1 &gt; Merkmale (zu Adresse)</code>)</p>
<p>Hinter jedem Eintrag, z.B. &quot;sz = schlechter Zahler&quot;, steht ausserdem eine Zahl, die sich jedes Mal, wenn ein Kärtchen mit dem Merkmal an eine Adresskarte geheftet wird, um eins erhöht und sich um eins reduziert, wenn ein Merkmalkärtchen irgendwo von der Adresskarte weggenommen wird. So genügt ein Blick in die Merkmal-Tabelle, um zu wissen, wie oft ein bestimmtes Merkmal vorkommt (wieviele schlechte Zahler vermerkt sind).</p>
<p>Die winzigen Kärtchen mit den Merkmalcodes befinden sich im Merkmal-Fenster zu jeder Adresse (Pfad: <code v-pre>Adressverwaltung &gt; Adressenstamm &gt; ./. &gt; Merkmale</code>).</p>
<p>Auch die Ereignisse werden nach dem obigen Verfahren mit Adressen verknüpft. Doch die Liste der möglichen Ereignisse (Pfad: Tabellen &gt; Adressverwaltung1 &gt; Ereignisse) enthält nicht nur ein Zahlenfeld, in dem die Anzahl Verknüpfungen festgehalten wird. Auch der jeweilige Wert wird aufaddiert, für den Fall, dass der Zahlungseingang aufgrund eines Spendenaufrufs als Ereignis definiert wird. Damit ist jederzeit ersichtlich, wieviel Geld auf den Aufruf hin eingegangen ist. Ausserdem lässt sich hier bestimmen, ob das Ereignis überhaupt mit einem Kärtchen bei der betreffenden Adresskarte vermerkt werden soll - wenn nicht, wird das jeweilige Eintreten des Ereignisses nur gezählt bzw. aufaddiert.</p>
<p>Aktionen sind wie Merkmale oder Ereignisse mit Adressen verknüpft; sie sind aber zugleich und zwingend mit Projekten verbunden. Mit anderen Worten: Aktionen beziehen sich immer auf Adressen und Projekte. Deshalb erscheinen auch drei Fenster auf dem Bildschirm, nämlich im oberen die Adresse, in der Mitte die Aktion(en) und unten das Projekt (Pfad: <code v-pre>Adressverwaltung &gt; Adressenstamm &gt; ./. &gt; Aktionen</code>). Die Projekte sind über Pfad: <code v-pre>Projektverwaltung &gt; Projekte</code> erreichbar.</p>
<p>Nach dem gleichen Prinzip wie die Personenmerkmale werden übrigens auch die Umsätze (Beiträge, Spenden, Warenumsatz) mit der Stammkarte einer Adresse gespeichert (Pfad: <code v-pre>Adressverwaltung &gt; Adressenstamm &gt; ./. Umsätze</code>).</p>
<p>Der zweite wichtige Karteikasten ist der Artikelstamm. Auch hier können diverse Informationen in gleicher Weise an die Artikelstammkarte geheftet werden: Merkmale, Lieferantenartikel, Bestellungen, Partien oder Umsätze.</p>
<p>Auf welche Weise können nun die diversen Informationen mit den Stammdaten verknüpft werden? Die meisten Verknüpfungen werden automatisch vorgenommen. Die Methode, die in mehreren Programmbereichen angewendet wird, sei an einem Beispiel erklärt.</p>
<p>Angenommen, man möchte festhalten, wieviele Personen aus dem Werbemittel Xyz bestellen. Bei der Auftragserfassung wird durch die Mediennummer registriert, aus welchem Werbemedium bestellt wurde. In der Medientabelle ist definiert, welches Ereignis die Bestellung aus Medium X darstellt. Bei der Verarbeitung (Fakturierung) eines Auftrags wird die Menge in der Tabelle um eins und das Wert-Feld um den Auftragsbetrag erhöht.</p>
<p>Nun werden nicht nur Menge und Wert gezählt, sondern auch eine Verknüpfung des Ereignisses mit der Auftraggeber-Adresse hergestellt. Im Ereignis-Fenster bei den betreffenden Adressen findet man dann folgende Information: <code v-pre>2345 01.07.1997 Bestellung aus Werbemittel Xyz 124.50</code></p>
<p>Die Ereignistabelle ist also zugleich ein Steuerungsinstrument. (Eine genauere Beschreibung der weiteren, hier nicht genannten Steuerungsmöglichkeiten finden Sie in der <RouteLink to="/manual/user/funktionen.html" title="Funktionsübersicht">Funktionsübersicht</RouteLink>.)</p>
<h2 id="_6-selektion-uber-alle-stamm-und-bewegungsdaten" tabindex="-1"><a class="header-anchor" href="#_6-selektion-uber-alle-stamm-und-bewegungsdaten"><span>6. Selektion über alle Stamm- und Bewegungsdaten</span></a></h2>
<p>Adressen und Artikel lassen sich mit sog. Filtern nach allen Stamm- und Zusatzinformationen suchen und selektieren. In einem Filter werden mehrere, mit einander kombinierbare Auswahlbedingungen zusammengefasst. So lässt sich beispielsweise ein Filter bilden, der alle Personen aus dem gesamten Adressbestand siebt, bei denen</p>
<ul>
<li>der Ort &quot;Bern&quot; lautet,</li>
<li>das Merkmal &quot;Abc&quot; vorkommt,</li>
<li>bei denen das Ereignis &quot;3001 Erhalt Mailing XYZ&quot;,</li>
<li>bei denen das Ereignis &quot;3002 Zahlungseingang auf Mailing XYZ&quot; zwischen dem 1.9.98 und 30.06.98 angefallen ist,</li>
<li>die im Jahr 1998 zwischen 300 und 1000 Franken gespendet haben.</li>
</ul>
</div></template>


